import {
  Box,
  Header,
  SpaceBetween,
  Cards,
  Link,
  ColumnLayout,
  Pagination,
  CollectionPreferences,
  CollectionPreferencesProps,
  Table,
  Button,
  Modal,
  Container,
  Grid,
} from '@cloudscape-design/components';

import { DashboardViewLayout } from '../../../components/DashboardViewLayout';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { LoadingOrError } from '../../../components/Loading';
import { getContact } from '../../../common/api/contacts';
import { PlaceContactWithReviews, ReviewContact } from '../../../common/types';
import { ReviewText } from '../../../components/ReviewText';
import { ReviewContent } from '../../../components/ReviewContent';
import { useMemo } from 'react';
import { formatDateString } from '../../../common/formatting/dateFormat';
import { formatPhoneNumber } from '../../../common/formatting/phoneFormat';
import { PhoneNumber } from '../../../components/PhoneNumber';
import { Email } from '../../../components/Email';
import { ContactSMSChatView } from '../../../components/SMSChatView';
import { ContactReviewsView } from './reviews';
import { formatName } from '../../../common/names';

export default function View() {
  const navigate = useNavigate();
  const resourceName = 'Contacts';
  const endpointName = 'contacts';
  const { uuid } = useParams({ from: '/dashboard/contacts_/$uuid' });

  // wait for the meta query to finish before proceeding
  const { isPending, isFetching, error, data, refetch } = useQuery({
    queryKey: [endpointName, uuid],
    queryFn: async () => (uuid ? await getContact(uuid) : null),
    // 10 minutes
    staleTime: 600_000,
  });

  const loading = (isPending || isFetching) && !error;

  return (
    <DashboardViewLayout
      headerVariant="high-contrast"
      breadcrumbs={[
        { text: resourceName, href: '/dashboard/contacts' },
        { text: data ? (data.first_name ?? '') + ' ' + (data.last_name ?? '') : '', href: '' },
      ]}
      header={
        data && (
          <Container
            header={
              <Header
                variant="h1"
                description={
                  <SpaceBetween direction="vertical" size="xxxs">
                    <Link
                      href={`/dashboard/places/${data.place.uuid}`}
                      variant="secondary"
                      onFollow={(e) => {
                        e.preventDefault();
                        navigate({ to: `/dashboard/places/${data.place.uuid}` });
                      }}
                    >
                      {data.place.name}
                    </Link>
                    <Box variant="p">
                      {data.review_contacts.length} mentions{' '}
                      {data.date ? `(last mentioned ${formatDateString(data.date)})` : ''}
                    </Box>
                  </SpaceBetween>
                }
              >
                {formatName(data.first_name, data.last_name)}
                {data.title ? ` - ${data.title}` : ''}
              </Header>
            }
          >
            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Phone Number</Box>
                <PhoneNumber
                  phoneNumber={data.phone_number}
                  lineTypeInfo={data.phone_line_type_info}
                  showLineTypeInfo
                />
              </div>
              <div>
                <Box variant="awsui-key-label">Email</Box>
                <Email email={data.email} />
              </div>
              <div>
                <Box variant="awsui-key-label">Department</Box>
                <Box variant="p">{data.department ?? '-'}</Box>
              </div>
            </ColumnLayout>
          </Container>
        )
      }
    >
      {loading || error ? (
        <LoadingOrError loading={loading} error={error} retry={refetch} />
      ) : data ? (
        <ContactView contact={data} />
      ) : null}
    </DashboardViewLayout>
  );
}

export type ContactViewProps = {
  contact: PlaceContactWithReviews;
};

export function ContactView(props: ContactViewProps) {
  const navigate = useNavigate();
  const { contact } = props;

  const sortedReviewContacts = useMemo(() => {
    return contact.review_contacts.sort((a: ReviewContact, b: ReviewContact) => {
      return (b.date ? new Date(b.date).getTime() : 0) - (a.date ? new Date(a.date).getTime() : 0);
    });
  }, [contact.review_contacts]);

  return (
    <Grid gridDefinition={[{ colspan: { default: 12, s: 4 } }, { colspan: { default: 12, s: 8 } }]}>
      <SpaceBetween direction="vertical" size="l">
        <ContactSMSChatView uuid={contact.uuid} />
      </SpaceBetween>
      <ContactReviewsView contact={contact} contacts={sortedReviewContacts} />
    </Grid>
  );
}
